import type {
  KindCondition,
  Nullable,
  Nullish,
  PriceId,
  WeightValue,
} from '@iheartjane/types';
import compact from 'lodash-es/compact';
import isEqual from 'lodash-es/isEqual';

import type { ExclusionOverlap } from '../conditions.types';

export const FOLDABLE_KEYS = [
  'kinds',
  'brand_ids',
  'brands',
  'lineages',
  'weights',
  'weight_values',
] as const;

export type FoldableKey = (typeof FOLDABLE_KEYS)[number];

/**
 * The keys an inclusion or exclusion rule can fold on, loose enough to accept
 * both the legacy form state and a special's rule set entries
 */
export type FoldableRule = {
  brand_ids?: Nullish<number[]>;
  brands?: Nullish<string[]>;
  kinds?: Nullish<KindCondition[]>;
  lineages?: Nullish<string[]>;
  weight_values?: Nullish<WeightValue[]>;
  weights?: Nullish<PriceId[]>;
};

interface Fold<T> {
  excludes: T;
  includes: T;
}

type Folds = {
  [K in FoldableKey]: Nullable<Fold<NonNullable<FoldableRule[K]>>>;
};

/** Inclusions that no exclusion rules out */
type Remaining<T> = (included: T[], excluded: T[]) => T[];

const remainingFlatList = <T>(included: T[], excluded: T[]) =>
  included.filter(
    (value) => !excluded.some((exclusion) => isEqual(value, exclusion))
  );

/**
 * Rules match products that meet the inclusions AND miss the exclusions, so
 * within a flat list, any exclusion is either redundant or, if also included,
 * better expressed by removing it from the inclusions.
 */
const foldFlatList = <T>(
  included: Nullish<T[]>,
  excluded: Nullish<T[]>
): Nullable<Fold<T[]>> => {
  if (!included?.length || !excluded?.length) return null;

  const remaining = remainingFlatList(included, excluded);

  // An empty list means "no restriction", so emptying it would widen the rule
  if (!remaining.length) return null;

  return { includes: remaining, excludes: [] };
};

const toPath = ({ kind, root_subtype, brand_subtype }: KindCondition) =>
  compact([kind, root_subtype, brand_subtype]);

/** Whether `category` is `ancestor` itself or sits somewhere beneath it */
const isWithin = (category: KindCondition, ancestor: KindCondition) => {
  const path = toPath(category);
  return toPath(ancestor).every((segment, index) => path[index] === segment);
};

const remainingCategories = (
  included: KindCondition[],
  excluded: KindCondition[]
) =>
  included.filter(
    (category) => !excluded.some((exclusion) => isWithin(category, exclusion))
  );

/**
 * Categories nest, so an exclusion beneath an included category (Edible minus
 * Edible/Chocolate) still narrows the rule and has to stay.
 */
const foldCategories = (
  included: Nullish<KindCondition[]>,
  excluded: Nullish<KindCondition[]>
): Nullable<Fold<KindCondition[]>> => {
  if (!included?.length || !excluded?.length) return null;

  const remaining = remainingCategories(included, excluded);

  if (!remaining.length) return null;

  const nestedExclusions = excluded.filter((exclusion) =>
    remaining.some((category) => isWithin(exclusion, category))
  );

  const isUnchanged =
    remaining.length === included.length &&
    nestedExclusions.length === excluded.length;
  if (isUnchanged) return null;

  return { includes: remaining, excludes: nestedExclusions };
};

const foldKeys = (include: FoldableRule, exclude: FoldableRule): Folds => ({
  kinds: foldCategories(include.kinds, exclude.kinds),
  brand_ids: foldFlatList(include.brand_ids, exclude.brand_ids),
  brands: foldFlatList(include.brands, exclude.brands),
  lineages: foldFlatList(include.lineages, exclude.lineages),
  weights: foldFlatList(include.weights, exclude.weights),
  weight_values: foldFlatList(include.weight_values, exclude.weight_values),
});

const excludesEveryInclusion = <T>(
  included: Nullish<T[]>,
  excluded: Nullish<T[]>,
  remaining: Remaining<T>
) =>
  !!included?.length &&
  !!excluded?.length &&
  !remaining(included, excluded).length;

const conflictingKeys = (
  include: FoldableRule,
  exclude: FoldableRule
): Record<FoldableKey, boolean> => ({
  kinds: excludesEveryInclusion(
    include.kinds,
    exclude.kinds,
    remainingCategories
  ),
  brand_ids: excludesEveryInclusion(
    include.brand_ids,
    exclude.brand_ids,
    remainingFlatList
  ),
  brands: excludesEveryInclusion(
    include.brands,
    exclude.brands,
    remainingFlatList
  ),
  lineages: excludesEveryInclusion(
    include.lineages,
    exclude.lineages,
    remainingFlatList
  ),
  weights: excludesEveryInclusion(
    include.weights,
    exclude.weights,
    remainingFlatList
  ),
  weight_values: excludesEveryInclusion(
    include.weight_values,
    exclude.weight_values,
    remainingFlatList
  ),
});

/**
 * How each key's exclusions overlap its inclusions. `redundant` ones fold away
 * without changing what the rule matches; `conflicting` ones rule out every
 * inclusion, so the rule matches no products.
 */
export const getExclusionOverlaps = (
  include: Nullish<FoldableRule>,
  exclude: Nullish<FoldableRule>
): Partial<Record<FoldableKey, ExclusionOverlap>> => {
  if (!include || !exclude) return {};

  const folds = foldKeys(include, exclude);
  const conflicts = conflictingKeys(include, exclude);

  return FOLDABLE_KEYS.reduce<Partial<Record<FoldableKey, ExclusionOverlap>>>(
    (overlaps, key) => {
      if (conflicts[key]) return { ...overlaps, [key]: 'conflicting' };
      if (folds[key]) return { ...overlaps, [key]: 'redundant' };
      return overlaps;
    },
    {}
  );
};

const applyFold = <K extends FoldableKey>(
  key: K,
  fold: Folds[K],
  include: FoldableRule,
  exclude: FoldableRule
) => {
  if (!fold) return;

  include[key] = fold.includes;
  if (fold.excludes.length) {
    exclude[key] = fold.excludes;
  } else {
    delete exclude[key];
  }
};

/**
 * Rewrites an inclusion/exclusion pair so that categories, brands, lineages,
 * and weights are expressed by inclusions alone wherever that matches the same
 * products. Conflicting keys are left alone, since emptying their inclusions
 * would widen the rule to match everything instead.
 */
export const foldExclusions = <I extends FoldableRule, E extends FoldableRule>(
  include: I,
  exclude: E
): { exclude: E; include: I } => {
  const folds = foldKeys(include, exclude);
  if (FOLDABLE_KEYS.every((key) => !folds[key])) return { exclude, include };

  const nextInclude = { ...include };
  const nextExclude = { ...exclude };
  for (const key of FOLDABLE_KEYS) {
    applyFold(key, folds[key], nextInclude, nextExclude);
  }

  return { exclude: nextExclude, include: nextInclude };
};

/**
 * Drops the exclusions that rule out every inclusion, keeping the narrower
 * reading of a rule that otherwise matches no products
 */
export const dropConflictingExclusions = <E extends FoldableRule>(
  include: FoldableRule,
  exclude: E
): E => {
  const conflicts = conflictingKeys(include, exclude);
  if (FOLDABLE_KEYS.every((key) => !conflicts[key])) return exclude;

  const nextExclude = { ...exclude };
  // Widened so the loop can delete by key; E only ever narrows these keys
  const foldable: FoldableRule = nextExclude;
  for (const key of FOLDABLE_KEYS) {
    if (conflicts[key]) delete foldable[key];
  }

  return nextExclude;
};
